import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Badge } from "./components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

const COLOR_CAPACITY = "#cbd5e1"; // slate-300
const COLOR_LEFT_OK = "#22c55e"; // green-500
const COLOR_LEFT_LOW = "#f97316"; // orange-500
const COLOR_LEFT_SOLD_OUT = "#ef4444"; // red-500

const formatShortDate = (dateString) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const truncate = (text, n = 28) =>
  !text ? "" : text.length > n ? text.slice(0, n - 1) + "…" : text;

// Split a title into up to two lines of ~n chars, breaking on spaces.
const wrapTitle = (text, n = 44) => {
  if (!text || text.length <= n) return [text || ""];
  const cut = text.lastIndexOf(" ", n);
  const at = cut > n * 0.5 ? cut : n;
  return [text.slice(0, at).trim(), truncate(text.slice(at).trim(), n)];
};

const colorForLeft = (left, capacity) => {
  if (capacity == null || capacity === 0) return COLOR_LEFT_OK;
  if (left <= 0) return COLOR_LEFT_SOLD_OUT;
  if (left / capacity <= 0.15) return COLOR_LEFT_LOW;
  return COLOR_LEFT_OK;
};

const LegendSwatch = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: color }} />
    {label}
  </span>
);

// Two-line category label on the left: event title + date.
const EventTick = ({ x, y, payload, rows }) => {
  const row = rows[payload.value];
  if (!row) return null;
  const lines = row.titleLines;
  const top = lines.length > 1 ? -12 : -3;
  return (
    <g transform={`translate(${x},${y})`} data-testid={`chart-label-${row.id}`}>
      <title>{row.fullTitle}</title>
      {lines.map((l, i) => (
        <text key={i} x={-8} y={top + i * 14} textAnchor="end" style={{ fontSize: 12, fill: "#1e293b", fontWeight: 600 }}>
          {l}
        </text>
      ))}
      <text x={-8} y={top + lines.length * 14 + 2} textAnchor="end" style={{ fontSize: 11, fill: "#64748b" }}>
        {row.dateLabel}
      </text>
    </g>
  );
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 shadow-md text-xs">
      <div className="font-semibold text-slate-800 mb-1">{row.fullTitle}</div>
      <div className="text-slate-500 mb-2">{row.dateLabel}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        <span className="text-slate-500">Capacity:</span>
        <span className="text-right font-medium">{row.capacity}</span>
        <span className="text-slate-500">Places booked:</span>
        <span className="text-right font-medium">{row.booked}</span>
        <span className="text-slate-500">Places available:</span>
        <span className="text-right font-medium">{row.left}</span>
        <span className="text-slate-500">Utilization:</span>
        <span className="text-right font-semibold text-indigo-700">{row.utilization}%</span>
      </div>
    </div>
  );
};

export const EventCapacityChart = ({
  isOpen,
  onClose,
  events = [],
  eventAvailability = {},
}) => {
  const [sortBy, setSortBy] = useState("date");
  const data = useMemo(() => {
    const rows = events
      .filter((e) => e && e.max_capacity)
      .map((e) => {
        const avail = eventAvailability[e.id];
        const capacity = Number(e.max_capacity) || 0;
        const left =
          typeof avail?.remaining_tickets === "number"
            ? avail.remaining_tickets
            : capacity;
        const booked = Math.max(0, capacity - left);
        const utilization =
          capacity > 0 ? Math.round((booked / capacity) * 100) : 0;
        return {
          id: e.id,
          fullTitle: e.title,
          dateLabel: formatShortDate(e.event_date),
          titleLines: wrapTitle(e.title, 44),
          capacity,
          left,
          booked,
          utilization,
          barLabel: `${booked} booked · ${left} left`,
          time: new Date(e.event_date).getTime() || 0,
        };
      });
    return sortBy === "fullness"
      ? rows.sort((a, b) => b.utilization - a.utilization || a.time - b.time)
      : rows.sort((a, b) => a.time - b.time);
  }, [events, eventAvailability, sortBy]);

  const rowsById = useMemo(
    () => Object.fromEntries(data.map((d) => [d.id, d])),
    [data]
  );

  const totalCapacity = data.reduce((s, d) => s + d.capacity, 0);
  const totalBooked = data.reduce((s, d) => s + d.booked, 0);
  const overallUtilization =
    totalCapacity > 0 ? Math.round((totalBooked / totalCapacity) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-5xl"
        data-testid="event-capacity-chart-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center justify-between gap-3 pr-6">
            <span>Bookings Overview — Upcoming Events</span>
            <div className="flex flex-wrap items-center gap-2 text-sm font-normal">
              <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700">
                Total capacity: {totalCapacity}
              </Badge>
              <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">
                Places booked: {totalBooked}
              </Badge>
              <Badge variant="outline" className="bg-indigo-50 border-indigo-200 text-indigo-700">
                Utilization: {overallUtilization}%
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        {data.length > 1 && (
          <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="chart-sort-toggle">
            <span>Sort by:</span>
            {[["date", "Date"], ["fullness", "Most booked first"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSortBy(key)}
                className={`px-3 py-1 rounded-full border transition-colors ${
                  sortBy === key
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`chart-sort-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {data.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            No upcoming events with a defined capacity to display.
          </div>
        ) : (
          <div className="w-full max-h-[65vh] overflow-y-auto" data-testid="event-capacity-chart-scroll">
            <div
              className="w-full"
              style={{ height: 90 + data.length * 72 }}
              data-testid="event-capacity-chart-canvas"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ top: 8, right: 140, left: 8, bottom: 8 }}
                  barCategoryGap="22%"
                  barGap={2}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} />
                  <YAxis
                    type="category"
                    dataKey="id"
                    width={310}
                    interval={0}
                    tick={<EventTick rows={rowsById} />}
                  />
                  <Tooltip cursor={{ fill: "rgba(99, 102, 241, 0.06)" }} content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="top"
                    content={() => (
                      <div className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-600">
                        <LegendSwatch color={COLOR_CAPACITY} label="Total capacity" />
                        <LegendSwatch color={COLOR_LEFT_OK} label="Places available" />
                        <LegendSwatch color={COLOR_LEFT_LOW} label="< 15% left" />
                        <LegendSwatch color={COLOR_LEFT_SOLD_OUT} label="Sold out" />
                      </div>
                    )}
                  />
                  <Bar dataKey="capacity" name="Capacity" fill={COLOR_CAPACITY} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="left" name="Places available" radius={[0, 4, 4, 0]}>
                    {data.map((d) => (
                      <Cell key={d.id} fill={colorForLeft(d.left, d.capacity)} />
                    ))}
                    <LabelList
                      dataKey="barLabel"
                      content={({ x, y, width, height, value }) => (
                        <text
                          x={Number(x) + Number(width) + 6}
                          y={Number(y) + Number(height) / 2}
                          dominantBaseline="middle"
                          style={{ fontSize: 11, fill: "#475569", fontWeight: 600 }}
                        >
                          {value}
                        </text>
                      )}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {data.length > 0 && (
          <div className="mt-2 text-xs text-slate-500">
            Hover any bar to see the full event title, exact numbers, and utilization.
            Green = healthy availability · Orange = &lt;15% left · Red = sold out.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventCapacityChart;
